import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useApp } from './AppContext';

const SalonContext = createContext(null);

export function SalonProvider({ children }) {
  const navigate = useNavigate();
  const { isAuthenticated, currentUser: currentCognitoUser } = useAuth();
  const { 
    getSalons, 
    getSalonStaff, 
    getServices, 
  } = useApp();

  const [businessInfo, setBusinessInfo] = useState();
  const [employees, setEmployees] = useState();
  const [services, setServices] = useState();
  const [serviceTypeOptions, setServiceTypeOptions] = useState();
  const [isMissingSalon, setIsMissingSalon] = useState(null);
  const [onboardingStage, setOnboardingStage] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState();

  useEffect(() => {
    if (employees && !currentUser) {
      setCurrentUser(currentCognitoUser ? employees?.find(emp => emp.id === currentCognitoUser.userId) : null);
      setEmployees(employees.map(emp => ({
        ...emp,
        isSelf: currentCognitoUser ? emp.id === currentCognitoUser.userId : false
      })));
    }
  }, [currentCognitoUser, employees]);

  useEffect(() => {
    if (services) {
      setServiceTypeOptions([...new Set(services.map(service => service.service_type))]);
    }
  }, [services]);

  const hasNoBusinessHours = (salon) => {
    return !(
      salon.monday_open_time || salon.monday_end_time ||
      salon.tuesday_open_time || salon.tuesday_end_time ||
      salon.wednesday_open_time || salon.wednesday_end_time ||
      salon.thursday_open_time || salon.thursday_end_time ||
      salon.friday_open_time || salon.friday_end_time ||
      salon.saturday_open_time || salon.saturday_end_time ||
      salon.sunday_open_time || salon.sunday_end_time
    );
  }

  useEffect(() => {
    const fetchBusinessInfo = async () => {
      if (!isAuthenticated) return;

      try {
        setIsLoading(true);
        const salons = await getSalons();
        if (salons && salons[0]) {
          setBusinessInfo(salons[0]);

          if (hasNoBusinessHours(salons[0])) {
            setOnboardingStage('business-hours');
          }
          
          if (location.pathname.match(/^\/(login|signup|verify-email|forgot-password|change-password)$/)) {
            navigate('/admin/calendar');
          }
        } else if (location.pathname !== '/business-info') {
          navigate('/business-info');
        }

        setIsMissingSalon(false);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching salon:', error);
        setIsMissingSalon(true);
        if ((error.status === 404 || error.status === "404") && location.pathname !== '/business-info') {
          setOnboardingStage('business-info');
          navigate('/business-info');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchBusinessInfo();
  }, [isAuthenticated]);

  useEffect(() => {
    if (businessInfo) {
      if (!employees){
        setIsLoading(true);
        getSalonStaff(businessInfo.id).then(staff => {
          console.log("Staff from SalonContext.js", staff);
          setEmployees(staff);
          setIsLoading(false);
        });
      }
      if (!services){
        setIsLoading(true);
        getServices(businessInfo.id).then(services => {
          console.log("Services from SalonContext.js", services);
          setServices(services);
          if (!onboardingStage && services.length === 0) {
            setOnboardingStage('add-services');
          }
          setIsLoading(false);
        });
      }
    }
  }, [businessInfo]);

  const value = {
    businessInfo,
    employees,
    services,
    isMissingSalon,
    onboardingStage,
    isLoading,
    currentUser,
    serviceTypeOptions,
    setBusinessInfo,
    setEmployees,
    setServices,
    setOnboardingStage,
    setIsMissingSalon,
    setCurrentUser
  };

  return (
    <SalonContext.Provider value={value}>
      {children}
    </SalonContext.Provider>
  );
}

export const useSalon = () => {
  const context = useContext(SalonContext);
  if (!context) {
    throw new Error('useSalon must be used within a SalonProvider');
  }
  return context;
}; 